import { useEffect, useState, useRef, useCallback } from "react";
import { toast } from "sonner";
import { ShieldCheck, Unplug, RefreshCw, CheckCircle2, Loader2, Circle, AlertTriangle, Lock } from "lucide-react";
import api from "../../lib/api";
import { PageHeader, StatusBadge, money } from "../../components/dashboard/Shared";
import ContactSupportModal from "../../components/ContactSupportModal";
import UpgradeRequestModal from "../../components/UpgradeRequestModal";

const STAGES = [
  { key: "creating", label: "Connecting to MetaApi..." },
  { key: "deploying", label: "Deploying MT5 account..." },
  { key: "synchronizing", label: "Synchronizing account..." },
  { key: "fetching", label: "Fetching account data..." },
];
const ORDER = { creating: 0, deploying: 1, synchronizing: 2, fetching: 3, connected: 4 };

export default function MT5Connection() {
  const [state, setState] = useState(null);
  const [sub, setSub] = useState(null);
  const [supportOpen, setSupportOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [form, setForm] = useState({ broker: "", login: "", password: "", server: "", account_type: "real", declared_equity: "" });
  const [brokers, setBrokers] = useState([]);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/investor/mt5");
      setState(data);
      return data;
    } catch (e) {
      setState({ connected: false, status: null });
      return null;
    }
  }, []);

  const startPolling = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const data = await load();
      if (data && data.status !== "connecting") {
        clearInterval(pollRef.current);
        pollRef.current = null;
        if (data.status === "connected") toast.success("MT5 account connected — live data synced");
        if (data.status === "failed") toast.error("MetaApi connection failed");
      }
    }, 3000);
  }, [load]);

  useEffect(() => {
    load().then((d) => { if (d?.status === "connecting") startPolling(); });
    api.get("/public/brokers").then(({ data }) => setBrokers(data)).catch(() => {});
    api.get("/subscription/me").then(({ data }) => setSub(data)).catch(() => setSub({}));
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [load, startPolling]);

  const connect = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, declared_equity: form.declared_equity === "" ? null : parseFloat(form.declared_equity) };
      await api.post("/investor/mt5/connect", payload);
      await load();
      startPolling();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Connection failed");
    } finally { setBusy(false); }
  };

  const retry = async () => {
    try {
      await api.post("/investor/mt5/retry");
      await load();
      startPolling();
    } catch (err) { toast.error(err.response?.data?.detail || "Retry failed"); }
  };

  const sync = async () => {
    setBusy(true);
    try {
      await api.post("/investor/mt5/sync");
      await load();
      toast.success("Live data refreshed from MetaApi");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sync failed");
    } finally { setBusy(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect and remove this MT5 account from MetaApi?")) return;
    await api.delete("/investor/mt5/disconnect");
    toast.success("Account disconnected");
    load();
  };

  if (!state) return <div className="skeleton h-80" />;

  // PENDING ADMIN APPROVAL — new flow: admin must approve before MetaApi connect fires
  if (state.status === "pending_approval") {
    return (
      <div className="max-w-xl">
        <PageHeader title="MT5 Connection" sub="Pending admin approval" />
        <div className="card-dark p-8 border-[#FFD600]/40" data-testid="mt5-pending-approval-card">
          <div className="flex items-center gap-3 mb-4">
            <Loader2 size={20} className="text-[#FFD600] animate-spin" />
            <p className="text-white">Awaiting admin review</p>
          </div>
          <p className="text-sm text-gray-400 leading-relaxed">
            Your MT5 account (<span className="text-white">{state.account?.broker} · {state.account?.login}</span>) has been submitted. Our compliance team reviews new connections manually — usually within a few hours.
            You&apos;ll receive an email once your account is approved and the MetaApi connection begins.
          </p>
          <button data-testid="mt5-remove-button" onClick={disconnect} className="btn-outline px-5 py-2 text-sm text-[#FF3B30] mt-6">Withdraw Submission</button>
        </div>
      </div>
    );
  }

  // REJECTED
  if (state.status === "rejected" || state.stage === "rejected") {
    return (
      <div className="max-w-xl">
        <PageHeader title="MT5 Connection" sub="Submission rejected" />
        <div className="card-dark p-8 border-[#FF3B30]/40" data-testid="mt5-rejected-card">
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle size={20} className="text-[#FF3B30]" />
            <p className="text-white">MT5 submission rejected</p>
          </div>
          <p className="text-sm text-gray-400 leading-relaxed">Reason: {state.account?.admin_approval_notes || "Contact support"}.</p>
          <button data-testid="mt5-remove-button" onClick={disconnect} className="btn-outline px-5 py-2 text-sm text-[#FF3B30] mt-6">Remove &amp; Retry</button>
        </div>
      </div>
    );
  }

  // CONNECTING: staged progress, no redirect until synchronized
  if (state.status === "connecting") {
    const cur = ORDER[state.stage] ?? 0;
    return (
      <div className="max-w-xl">
        <PageHeader title="MT5 Connection" sub={`${state.account?.broker} · ${state.account?.login}`} />
        <div className="card-dark p-8" data-testid="mt5-connecting-card">
          <p className="label-xs mb-6">Establishing live connection</p>
          <div className="space-y-5">
            {STAGES.map((s, i) => (
              <div key={s.key} className="flex items-center gap-4" data-testid={`stage-${s.key}`}>
                {i < cur ? <CheckCircle2 size={20} className="text-[#00E676] shrink-0" /> :
                 i === cur ? <Loader2 size={20} className="text-[#00F0FF] animate-spin shrink-0" /> :
                 <Circle size={20} className="text-gray-700 shrink-0" />}
                <span className={`text-sm ${i === cur ? "text-white" : i < cur ? "text-gray-400" : "text-gray-600"}`}>{s.label}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-8 leading-relaxed">
            Deploying a cloud terminal and synchronizing with your broker can take 1–5 minutes. Keep this page open — you&apos;ll only see dashboard data once real account data is received from MetaApi.
          </p>
        </div>
      </div>
    );
  }

  // FAILED: exact MetaApi error
  if (state.status === "failed") {
    return (
      <div className="max-w-xl">
        <PageHeader title="MT5 Connection" sub="Connection failed" />
        <div className="card-dark p-8 border-[#FF3B30]/40" data-testid="mt5-failed-card">
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle size={20} className="text-[#FF3B30]" />
            <p className="text-white">MetaApi connection failed</p>
          </div>
          <p className="label-xs mb-2">Exact MetaApi error</p>
          <pre data-testid="mt5-error-message" className="text-xs text-[#FF3B30] bg-black/40 border border-white/10 rounded-lg p-4 whitespace-pre-wrap break-all max-h-60 overflow-auto">{state.last_error || "Unknown error"}</pre>
          <div className="flex gap-3 mt-6">
            <button data-testid="mt5-retry-button" onClick={retry} className="btn-primary px-5 py-2 text-sm flex items-center gap-2"><RefreshCw size={14} /> Retry Connection</button>
            <button data-testid="mt5-remove-button" onClick={disconnect} className="btn-outline px-5 py-2 text-sm text-[#FF3B30]">Remove &amp; Start Over</button>
          </div>
        </div>
      </div>
    );
  }

  // CONNECTED: real MetaApi data
  if (state.connected) {
    const a = state.account;
    return (
      <div>
        <PageHeader title="MT5 Connection" sub="Live data from MetaApi Cloud" />
        <div className="card-dark p-6 max-w-2xl" data-testid="mt5-connected-card">
          <div className="flex items-center justify-between mb-6">
            <p className="text-white font-medium">Connected Account</p>
            <StatusBadge status="connected" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            {[["Broker", a.broker], ["MT5 Login", a.login], ["Server", a.server],
              ["Currency", a.currency || "—"], ["Leverage", a.leverage ? `1:${a.leverage}` : "—"],
              ["Balance", a.balance != null ? money(a.balance) : "—"],
              ["Equity", a.equity != null ? money(a.equity) : "—"],
              ["Free Margin", a.free_margin != null ? money(a.free_margin) : "—"],
              ["MetaApi Account ID", a.metaapi_account_id],
              ["Last Synced", a.last_synced_at ? String(a.last_synced_at).slice(0, 19).replace("T", " ") : "—"]].map(([k, v]) => (
              <div key={k} className="flex justify-between border-b border-white/5 py-2 gap-4">
                <span className="text-gray-500 shrink-0">{k}</span><span className="tabular text-gray-200 truncate">{v}</span>
              </div>
            ))}
          </div>
          <div className="flex gap-3 mt-6 flex-wrap">
            <button data-testid="mt5-sync-button" onClick={sync} disabled={busy} className="btn-primary px-5 py-2 text-sm flex items-center gap-2 disabled:opacity-50">
              <RefreshCw size={14} className={busy ? "animate-spin" : ""} /> {busy ? "Syncing…" : "Sync Now"}
            </button>
            <button data-testid="mt5-disconnect-button" onClick={disconnect} className="btn-outline px-5 py-2 text-sm flex items-center gap-2 text-[#FF3B30]">
              <Unplug size={15} /> Disconnect
            </button>
          </div>
        </div>
      </div>
    );
  }

  // NO ACCOUNT: show the MT5 connect form directly (no KYC gate)
  return (
    <div>
      <PageHeader title="MT5 Connection" sub="Link your broker account — live via MetaApi Cloud" />
      {sub && (() => {
        const noSub = !sub.has_subscription;
        const st = sub.status;
        const activeStatus = st === "active" || st === "lifetime";
        const limitReached = !noSub && activeStatus && sub.mt5_limit >= 0 && sub.mt5_used >= sub.mt5_limit;
        if (noSub || !activeStatus) {
          return (
            <div className="card-dark border-yellow-500/40 p-5 mb-6 flex flex-wrap items-center justify-between gap-4" data-testid="no-subscription-banner">
              <div className="flex items-start gap-3">
                <Lock className="w-5 h-5 text-yellow-400 mt-0.5" />
                <div>
                  <p className="text-white text-sm">Account eligibility &amp; subscription required</p>
                  <p className="text-xs text-gray-400 mt-1">MT5 connection unlocks after approval. Eligibility: minimum <b className="text-gray-200">USD $1,000</b> equity · profit share <b className="text-gray-200">50/50</b> of net profits · settlement every <b className="text-gray-200">Friday</b>. Activation can take up to 24 hours after a verified connection.</p>
                </div>
              </div>
              <button onClick={() => setUpgradeOpen(true)} className="btn-primary px-5 py-2 text-sm" data-testid="activate-plan-btn">Request Subscription Upgrade</button>
            </div>
          );
        }
        if (limitReached) {
          return (
            <div className="card-dark border-[#FF3B30]/40 p-5 mb-6 flex flex-wrap items-center justify-between gap-4" data-testid="limit-reached-banner">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-[#FF3B30] mt-0.5" />
                <div>
                  <p className="text-white text-sm">Plan limit reached. Upgrade your subscription.</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {sub.plan_name} plan · {sub.mt5_used} of {sub.mt5_limit_display} MT5 accounts connected.
                  </p>
                </div>
              </div>
              <button onClick={() => setUpgradeOpen(true)} className="btn-primary px-5 py-2 text-sm" data-testid="upgrade-plan-btn">Request Subscription Upgrade</button>
            </div>
          );
        }
        return (
          <div className="card-dark p-4 mb-6 flex items-center justify-between text-xs">
            <p className="text-gray-400">Active plan: <span className="text-white">{sub.plan_name}</span> — <span className="text-[#00F0FF]">{sub.mt5_used}</span> / {sub.mt5_limit_display} MT5 accounts used</p>
            <span className="text-[#00E676]">✓ Ready to connect</span>
          </div>
        );
      })()}
      <div className="grid lg:grid-cols-2 gap-8 max-w-4xl">
        <form onSubmit={connect} className="card-dark p-6 space-y-4" data-testid="mt5-connect-form">
          <div>
            <label className="label-xs block mb-2">Broker Name</label>
            <input data-testid="mt5-broker-input" required list="broker-list" className="input-dark" placeholder="e.g. IC Markets"
              value={form.broker} onChange={(e) => setForm({ ...form, broker: e.target.value })} />
            <datalist id="broker-list">{brokers.map((b) => <option key={b.broker_id} value={b.name} />)}</datalist>
          </div>
          <div>
            <label className="label-xs block mb-2">MT5 Login</label>
            <input data-testid="mt5-login-input" required className="input-dark" placeholder="e.g. 5023481" value={form.login} onChange={(e) => setForm({ ...form, login: e.target.value })} />
          </div>
          <div>
            <label className="label-xs block mb-2">MT5 Password</label>
            <input data-testid="mt5-password-input" required type="password" className="input-dark" placeholder="••••••••" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          </div>
          <div>
            <label className="label-xs block mb-2">Server Name</label>
            <input data-testid="mt5-server-input" required className="input-dark" placeholder="e.g. ICMarketsSC-Live01" value={form.server} onChange={(e) => setForm({ ...form, server: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-xs block mb-2">Account Type</label>
              <select data-testid="mt5-account-type" className="input-dark" value={form.account_type} onChange={(e) => setForm({ ...form, account_type: e.target.value })}>
                <option value="real">Real / Live</option>
                <option value="demo">Demo</option>
              </select>
            </div>
            <div>
              <label className="label-xs block mb-2">Current Equity (USD)</label>
              <input data-testid="mt5-declared-equity" type="number" min="0" step="1" className="input-dark" placeholder="e.g. 1000" value={form.declared_equity} onChange={(e) => setForm({ ...form, declared_equity: e.target.value })} />
            </div>
          </div>
          <p className="text-[11px] text-gray-600 -mt-1">Your declared equity is shared with our review team to confirm eligibility. We never see or store your MT5 password in plain text.</p>
          {(() => {
            const noSub = !sub || !sub.has_subscription;
            const activeStatus = sub && (sub.status === "active" || sub.status === "lifetime");
            const limitReached = sub && activeStatus && sub.mt5_limit >= 0 && sub.mt5_used >= sub.mt5_limit;
            const disabled = busy || noSub || !activeStatus || limitReached;
            const label = noSub || !activeStatus ? "Subscription required"
                        : limitReached ? "Plan limit reached — Upgrade"
                        : busy ? "Starting…" : "Connect Account";
            return (
              <button data-testid="mt5-connect-button" disabled={disabled}
                className="btn-primary w-full py-2.5 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed">
                {label}
              </button>
            );
          })()}
        </form>
        <div className="space-y-4 text-sm text-gray-400">
          <div className="card-dark p-5 flex gap-3">
            <ShieldCheck size={20} className="text-[#00E676] shrink-0" strokeWidth={1.5} />
            <p className="leading-relaxed">Your credentials are AES-encrypted and used to provision a read-synchronized cloud terminal via MetaApi. ChartzFx can never move or withdraw your funds.</p>
          </div>
          <div className="card-dark p-5">
            <p className="text-white mb-2">What happens next</p>
            <p className="leading-relaxed text-gray-500">We create your account on MetaApi Cloud, deploy a terminal, wait for full synchronization with your broker, then pull your real balance, equity, positions and trade history. Nothing is shown until real data arrives.</p>
          </div>
        </div>
      </div>
      <ContactSupportModal open={supportOpen} onClose={() => setSupportOpen(false)} />
      <UpgradeRequestModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} />
    </div>
  );
}
